import { EmptyStateTable } from '@components'

import { Table } from '@shinetools/lumen-react'
import type { TableSortDirection } from '@shinetools/lumen-react'
import { KeyboardEvent, ReactElement, ReactNode, useCallback } from 'react'

import { SUPPLIERS_BALANCE_TABLE_MIN_WIDTH } from '../../constants/suppliersBalanceColumnWidths'
import type { SuppliersBalanceColumnDef } from '../../hooks/useSuppliersBalanceTableColumns'
import { SuppliersBalance } from '../../types/SuppliersBalance'
import { SuppliersBalanceEmptyState } from '../SuppliersBalanceEmptyState/SuppliersBalanceEmptyState'
import type { ItemsPerPage } from '../TableFooter'
import { TableFooter } from '../TableFooter'
import { TableHeader } from '../TableHeader'
import { TotalsFooterRow } from './elements/TotalsFooterRow'
import * as Styled from './styles'

interface SuppliersBalanceTableProps {
  columns: SuppliersBalanceColumnDef[]
  data: SuppliersBalance[]
  onSort: (columnId: string, isDesc: boolean) => void
  sortedColumnId: string | undefined
  onRowClick?: (row: SuppliersBalance) => void
  isLoading?: boolean
  currentPage: number
  itemsPerPage: ItemsPerPage
  totalPageItems: number
  onPageChange: (page: number) => void
  onItemsPerPageChange: (itemsPerPage: ItemsPerPage) => void
  emptyRowInfo?: ReactNode
  queryKey: string
}

export const SuppliersBalanceTable = ({
  columns,
  data,
  onSort,
  sortedColumnId,
  onRowClick,
  isLoading = false,
  currentPage,
  itemsPerPage,
  totalPageItems,
  onPageChange,
  onItemsPerPageChange,
  emptyRowInfo,
  queryKey,
}: SuppliersBalanceTableProps): ReactElement => {
  const hasData = data.length > 0
  const isDesc = sortedColumnId?.startsWith('-') ?? false
  const activeColumnId = isDesc ? sortedColumnId?.slice(1) : sortedColumnId

  const handleSort = useCallback(
    (columnId: string) => {
      const isCurrentColumn = activeColumnId === columnId
      onSort(columnId, isCurrentColumn ? !isDesc : false)
    },
    [activeColumnId, isDesc, onSort],
  )

  const getSortDirection = useCallback(
    (columnId: string): TableSortDirection => {
      if (activeColumnId !== columnId) {
        return false
      }
      return isDesc ? 'descending' : 'ascending'
    },
    [activeColumnId, isDesc],
  )

  const handleRowKeyDown = useCallback(
    (row: SuppliersBalance) => (event: KeyboardEvent<HTMLTableRowElement>) => {
      if (event.key === 'Enter' || event.key === ' ') {
        event.preventDefault()
        onRowClick?.(row)
      }
    },
    [onRowClick],
  )

  const renderTableBody = (): ReactElement => (
    <Table.Body>
      {data.map((row) => (
        <Table.Row
          key={row.id}
          onClick={onRowClick ? () => onRowClick(row) : undefined}
          onKeyDown={onRowClick ? handleRowKeyDown(row) : undefined}
          tabIndex={onRowClick ? 0 : undefined}
          role={onRowClick ? 'button' : undefined}
        >
          {columns.map((column) => (
            <Table.Cell key={column.id} align={column.align} highlighted={column.highlighted}>
              {column.Cell(row)}
            </Table.Cell>
          ))}
        </Table.Row>
      ))}
      {hasData && <TotalsFooterRow columns={columns} />}
    </Table.Body>
  )

  const tableHeader = <TableHeader columns={columns} getSortDirection={getSortDirection} onSort={handleSort} />

  if (!hasData && !isLoading) {
    return (
      <EmptyStateTable header={tableHeader} minWidth={SUPPLIERS_BALANCE_TABLE_MIN_WIDTH}>
        {emptyRowInfo ?? <SuppliersBalanceEmptyState />}
      </EmptyStateTable>
    )
  }

  return (
    <Styled.SuppliersBalanceTableWrapper>
      <Table compact>
        <Table.Content
          maxHeight="none"
          scrollable
          scrollResetKey={`${currentPage}-${itemsPerPage}-${sortedColumnId}-${queryKey}`}
          style={{ minWidth: SUPPLIERS_BALANCE_TABLE_MIN_WIDTH }}
        >
          {tableHeader}
          {isLoading ? <Table.Skeleton columns={columns.length} /> : renderTableBody()}
        </Table.Content>
        {hasData && (
          <TableFooter
            currentPage={currentPage}
            itemsPerPage={itemsPerPage}
            onItemsPerPageChange={onItemsPerPageChange}
            onPageChange={onPageChange}
            totalPageItems={totalPageItems}
          />
        )}
      </Table>
    </Styled.SuppliersBalanceTableWrapper>
  )
}
